/**
 * Training module query and mutation helpers using Gen 2 typed client.
 */
import { authClient } from "../utils/amplifyClient";

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

export async function listTrainingTopicsByCompany(companyId: string) {
    const { data } = await authClient.models.TrainingTopic.listTrainingTopicByCompanyId({ companyId });
    return data;
}

export async function listTrainingPlansByCompany(companyId: string) {
    const { data } = await authClient.models.TrainingPlan.listTrainingPlanByCompanyId({ companyId });
    return data;
}

export async function listTrainingSchedulesByPlan(planId: string) {
    const { data } = await authClient.models.TrainingSchedule.listTrainingScheduleByPlanIdAndScheduledDate({ planId });
    return data;
}

export async function listTrainingEnrollmentsByPlan(planId: string) {
    const { data } = await authClient.models.TrainingEnrollment.listTrainingEnrollmentByPlanId({ planId });
    return data;
}

export async function listTrainingEnrollmentsByUser(userId: string) {
    const { data } = await authClient.models.TrainingEnrollment.listTrainingEnrollmentByUserId({ userId });
    return data;
}

export async function listTrainingCompletionsByUser(userId: string) {
    const { data } = await authClient.models.TrainingCompletion.listTrainingCompletionByUserIdAndCompletionDate({ userId });
    return data;
}

export async function listTrainingCompletionsByPlan(planId: string) {
    const { data } = await authClient.models.TrainingCompletion.listTrainingCompletionByPlanId({ planId });
    return data;
}

export async function listTrainingAffidavitsByPlan(planId: string) {
    const { data } = await authClient.models.TrainingAffidavit.listTrainingAffidavitByPlanId({ planId });
    return data;
}

// ---------------------------------------------------------------------------
// Mutation helpers
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingTopic(input: any) {
    const { data } = await authClient.models.TrainingTopic.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateTrainingTopic(input: any) {
    const { data } = await authClient.models.TrainingTopic.update(input);
    return data;
}

export async function deleteTrainingTopic(id: string) {
    const { data } = await authClient.models.TrainingTopic.delete({ id });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingPlan(input: any) {
    const { data } = await authClient.models.TrainingPlan.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateTrainingPlan(input: any) {
    const { data } = await authClient.models.TrainingPlan.update(input);
    return data;
}

export async function deleteTrainingPlan(id: string) {
    const { data } = await authClient.models.TrainingPlan.delete({ id });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingSchedule(input: any) {
    const { data } = await authClient.models.TrainingSchedule.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateTrainingSchedule(input: any) {
    const { data } = await authClient.models.TrainingSchedule.update(input);
    return data;
}

export async function deleteTrainingSchedule(id: string) {
    const { data } = await authClient.models.TrainingSchedule.delete({ id });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingEnrollment(input: any) {
    const { data } = await authClient.models.TrainingEnrollment.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateTrainingEnrollment(input: any) {
    const { data } = await authClient.models.TrainingEnrollment.update(input);
    return data;
}

export async function deleteTrainingEnrollment(id: string) {
    const { data } = await authClient.models.TrainingEnrollment.delete({ id });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingCompletion(input: any) {
    const { data } = await authClient.models.TrainingCompletion.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingAffidavit(input: any) {
    const { data } = await authClient.models.TrainingAffidavit.create(input);
    return data;
}

// ---------------------------------------------------------------------------
// Library Templates (Gen2)
// ---------------------------------------------------------------------------

export async function listAllLibraryTopicTemplates() {
    const { data } = await authClient.models.LibraryTopicTemplate.list();
    return data;
}

export async function listAllLibraryPlanTemplates() {
    const { data } = await authClient.models.LibraryPlanTemplate.list();
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createLibraryTopicTemplate(input: any) {
    const { data } = await authClient.models.LibraryTopicTemplate.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createLibraryPlanTemplate(input: any) {
    const { data } = await authClient.models.LibraryPlanTemplate.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLibraryPlanTemplate(input: any) {
    const { data } = await authClient.models.LibraryPlanTemplate.update(input);
    return data;
}

// ---------------------------------------------------------------------------
// Quiz Responses (using raw GraphQL — model not yet in Gen2 schema)
// ---------------------------------------------------------------------------

const listQuizResponsesByCompanyQuery = /* GraphQL */ `
  query ListQuizResponsesByCompany($companyId: String!, $limit: Int, $nextToken: String) {
    trainingQuizResponsesByCompanyId(companyId: $companyId, limit: $limit, nextToken: $nextToken) {
      items {
        id companyId userId planId categoryId formId responseData signaturePath
        passed score totalQuestions attemptNumber completedAt cycleStartDate
      }
      nextToken
    }
  }
`;

const listQuizResponsesByUserQuery = /* GraphQL */ `
  query ListQuizResponsesByUser($userId: String!, $limit: Int, $nextToken: String) {
    trainingQuizResponsesByUserId(userId: $userId, limit: $limit, nextToken: $nextToken) {
      items {
        id companyId userId planId categoryId formId responseData signaturePath
        passed score totalQuestions attemptNumber completedAt cycleStartDate
      }
      nextToken
    }
  }
`;

const createQuizResponseMutation = /* GraphQL */ `
  mutation CreateTrainingQuizResponse($input: CreateTrainingQuizResponseInput!) {
    createTrainingQuizResponse(input: $input) {
      id companyId userId planId categoryId formId responseData signaturePath
      passed score totalQuestions attemptNumber completedAt cycleStartDate
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function listTrainingQuizResponsesByCompany(companyId: string): Promise<any[]> {
    const result = await authClient.graphql({
        query: listQuizResponsesByCompanyQuery,
        variables: { companyId },
    }) as any;
    return result.data.trainingQuizResponsesByCompanyId.items;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function listTrainingQuizResponsesByUser(userId: string): Promise<any[]> {
    const result = await authClient.graphql({
        query: listQuizResponsesByUserQuery,
        variables: { userId },
    }) as any;
    return result.data.trainingQuizResponsesByUserId.items;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createTrainingQuizResponse(input: any): Promise<any> {
    const result = await authClient.graphql({
        query: createQuizResponseMutation,
        variables: { input },
    }) as any;
    return result.data.createTrainingQuizResponse;
}
